import { Component, HostListener, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger, MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MoveWikiItemDialogComponent } from 'src/app/components/utils/move-wiki-item-dialog/move-wiki-item-dialog.component';
import { ApplicationEnvironment } from 'src/app/models/application_environment.model';
import { WikiNode } from 'src/app/models/wiki-node.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { InputDialogComponent } from '../../../shared/input-dialog/input-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WikiNodeCreateDialogComponent } from './wiki-node-create-dialog/wiki-node-create-dialog.component';
import { WikiArticleUpdateDialogComponent } from '../wiki-article/wiki-article-update-permissions-dialog/wiki-article-update-permissions-dialog.component';
import { PermissionsService } from 'src/app/services/permissions.service';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-wiki-node',
  templateUrl: './wiki-node.component.html',
  styleUrls: ['./wiki-node.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    RouterLink,
    MatIcon,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    MatIconButton,
    DatePipe,
  ],
})
export class WikiNodeComponent implements OnInit {
  protected activatedRoute = inject(ActivatedRoute);
  private api = inject(ApiService);
  private dialog = inject(MatDialog);
  private employee = inject(EmployeeService);
  private snack = inject(MatSnackBar);
  private permissions = inject(PermissionsService);

  wikiNodeId: string = null as any;
  wikiNode: WikiNode = null as any;

  menuTopLeftPosition = { x: '0', y: '0' };
  // reference to the MatMenuTrigger in the DOM
  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

  environments: ApplicationEnvironment[] = null as any;
  environment_selected: number = null as any;

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.environments = null as any;
      this.wikiNodeId = params.id;
      this.fetchWikiTree();
    });
  }

  isUserAdmin(): boolean {
    return this.permissions.isAdmin() ?? false;
  }

  getPathGithub(element: any) {
    const url = new URL(element.git_url);
    const url_arr = url.pathname.split('/');
    if (url_arr[1] === 'repos') {
      const org = url_arr[2];
      const repo = url_arr[3];
      const filename = element.name;
      return org + '/' + repo + '/' + filename;
    } else return '';
  }

  createNode() {
    const dialogRef = this.dialog.open(WikiNodeCreateDialogComponent, {
      width: 'auto',
      data: { wikiNodeId: this.wikiNode.id },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const newWikiNodeData = {
          name: result.name,
          wiki_node_id: this.wikiNode.id,
          allowed_department_ids: result.categories && result.categories.length > 0 ? result.categories : null,
        };
        this.api.createWikiNode(newWikiNodeData).subscribe(() => {
          this.fetchWikiTree();
          this.snack.open(`Folder "${result.name}" created successfully!`, 'Close', {
            duration: 3000,
          });
        });
      }
    });
  }

  createArticle() {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: '400px',
      data: {
        title: 'Título de nuevo documento',
        description: `Introduce el título del nuevo documento.`,
        type: 'text',
        placeholder: 'Título de ejemplo...',
        label: 'Nuevo título',
        close_button_text: 'Crear',
      },
    });

    dialogRef.afterClosed().subscribe(nombre => {
      if (nombre) {
        const newWikiNodeName = {
          title: nombre,
          wiki_node_id: this.wikiNode.id,
        };
        this.api.createWikiArticle(newWikiNodeName).subscribe((data: any) => {
          this.fetchWikiTree();
        });
      }
    });
  }

  onRightClick(event: MouseEvent, item: any, type: string) {
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';

    // we open the menu
    // we pass to the menu the information about our object
    item.type = type;
    this.contextMenu.menuData = { item: item };

    // we open the menu
    this.contextMenu.openMenu();
  }

  deleteNode() {
    if (!confirm('¿Estas seguro que quieres borrar esta carpeta? Se borraran todos los documentos que contenga.')) return;
    this.api.deleteWikiNode(this.contextMenu.menuData.item.id).subscribe((data: any) => {
      this.fetchWikiTree();
    });
  }

  editWikiNode(): void {
    const dialogRef = this.dialog.open(WikiNodeCreateDialogComponent, {
      width: 'auto',
      data: this.contextMenu.menuData.item,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const updatedData = {
          name: result.name,
          allowed_department_ids: result.categories && result.categories.length > 0 ? result.categories : null,
        };

        // Call an API or service method to update the folder with new data
        this.api.updateWikiNode(this.contextMenu.menuData.item.id, updatedData).subscribe(() => {
          this.fetchWikiTree(); // Refresh data after update
        });
      }
    });
  }

  deleteArticle() {
    if (!confirm('¿Estas seguro que quieres borrar este documento?')) return;
    this.api.deleteWikiArticle(this.contextMenu.menuData.item.id).subscribe((data: any) => {
      this.fetchWikiTree();
    });
  }

  editArticle() {
    const dialogRef = this.dialog.open(WikiArticleUpdateDialogComponent, {
      width: 'auto',
      data: this.contextMenu.menuData.item,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const updatedData = {
          allowed_department_ids: result.categories && result.categories.length > 0 ? result.categories : null,
        };
        this.api.updateWikiArticle(this.contextMenu.menuData.item.id, updatedData).subscribe((data: any) => {
          this.fetchWikiTree();
        });
      }
    });
  }

  showMoveWikiItemDialog() {
    const dialogRef = this.dialog.open(MoveWikiItemDialogComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(body => {
      this.moveWikiItem(body);
    });
  }

  moveWikiItem(wikiNode: WikiNode) {
    if (!this.contextMenu.menuData.item) return;
    if (this.contextMenu.menuData.item.type == 'WikiNode') {
      this.api
        .updateWikiNode(this.contextMenu.menuData.item.id, {
          wiki_node_id: wikiNode.id,
        })
        .subscribe((data: any) => {
          this.fetchWikiTree();
        });
    } else {
      this.api
        .updateWikiArticle(this.contextMenu.menuData.item.id, {
          wiki_node_id: wikiNode.id,
        })
        .subscribe((data: any) => {
          this.fetchWikiTree();
        });
    }
  }

  onEnvironmentChanged(env: number) {
    this.environment_selected = env;
    this.fetchWikiTree();
  }

  private fetchWikiTree() {
    this.api
      .getWikiNode(parseInt(this.wikiNodeId), {
        with: 'children..company_product,wiki_articles,parent,company_product',
        append: 'all_parents',
        application_environment_id: this.environment_selected,
      })
      .subscribe(data => {
        if (this.employee.isMeetingOngoing()) {
          data.children = data.children.filter(
            child => child.company_product == null || this.employee.isMeetingOngoing(child.company_product.id)
          );
        }

        this.wikiNode = data;
        if (this.wikiNode.company_product != null && this.environments == null) {
          this.fetchCompanyProductEnvironments(this.wikiNode.company_product.id);
        }
      });
  }

  private fetchCompanyProductEnvironments(company_product_id: number) {
    this.api.getCompanyProductApplicationEnvironments(company_product_id.toString()).subscribe(data => {
      this.environments = data;
    });
  }

  onLongPress(event: MouseEvent, element: WikiNode): void {
    event.preventDefault(); // Prevent default context menu
    this.openEditDialog(element); // Open the dialog in edit mode
  }

  openEditDialog(element: WikiNode): void {
    const dialogRef = this.dialog.open(WikiNodeCreateDialogComponent, {
      width: 'auto',
      panelClass: 'compact-dialog',
      data: { element }, // Pass the folder data to the dialog
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const updatedData = {
          name: result.name,
          allowed_department_ids: result.categories && result.categories.length > 0 ? result.categories : null,
        };

        // Call an API or service method to update the folder with new data
        this.api.updateWikiNode(element.id, updatedData).subscribe(() => {
          this.fetchWikiTree(); // Refresh data after update
        });
      }
    });
  }
}
